/**
 * Viewing Slide-over Alpine.js Component
 *
 * Manages the viewing detail slide-over panel state and data fetching.
 * Supports view, create, and edit modes.
 *
 * Events:
 *   - open-viewing-slideover: { id: viewingId } - Open to view existing viewing
 *   - open-viewing-slideover-create: { property_id, person_id, enquiry_id, ... } - Open to book new viewing
 */

import consumer from "../channels/consumer"

export function viewingSlideover() {
  return {
    // State
    open: false,
    loading: false,
    error: null,
    viewing: null,
    viewingId: null,
    subscription: null,
    zIndex: 100010,

    // Mode: 'view' for existing viewing, 'create' for new viewing, 'edit' for editing
    mode: 'view',

    // View mode action states
    showCancelForm: false,
    showRescheduleForm: false,
    showRejectForm: false,
    cancelReason: '',
    rejectReason: '',
    cancelling: false,
    rejecting: false,
    rescheduling: false,
    confirming: false,
    completing: false,
    markingNoShow: false,
    sendingPostViewingEmail: false,
    loadingPropertyDetails: false,
    viewNotes: '',
    savingNotes: false,

    // Reschedule form state
    rescheduleSlots: [],
    loadingRescheduleSlots: false,
    rescheduleDate: '',
    rescheduleTime: '',
    rescheduleUseCustomTime: false,
    rescheduleReason: '',

    // Create/Edit form state
    form: {
      property_id: null,
      property_search: '',
      property_results: [],
      searching_properties: false,
      show_property_dropdown: false,
      selected_property: null,
      person_id: null,
      person_search: '',
      person_results: [],
      searching_people: false,
      show_person_dropdown: false,
      selected_person: null,
      // New person form
      show_new_person_form: false,
      new_person: {
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      },
      enquiry_id: null,
      scheduled_at: '',
      scheduled_date: '',
      scheduled_time: '',
      use_custom_time: false,
      notes: '',
      // Pre-qualification form responses
      form_responses: {}
    },

    // Available slots state
    availableSlots: [],
    loadingSlots: false,

    // Form submission state
    saving: false,
    formErrors: {},

    // Accordion state
    openSections: {},

    // Show toast notification
    showNotification(message, type = 'success') {
      const alertClass = type === 'success'
        ? 'bg-green-100 border-green-400 text-green-700 dark:bg-green-900/30 dark:border-green-600 dark:text-green-300'
        : 'bg-red-100 border-red-400 text-red-700 dark:bg-red-900/30 dark:border-red-600 dark:text-red-300'

      const flashContainer = document.getElementById('flash')
      if (!flashContainer) return

      const notification = document.createElement('div')
      notification.className = `fixed top-20 border-l-4 p-4 rounded shadow-lg w-full md:max-w-md md:right-4 md:left-auto md:transform-none left-1/2 transform -translate-x-1/2 md:-translate-x-0 ${alertClass}`
      notification.style.top = '5rem'
      notification.style.zIndex = String(this.zIndex + 50)
      notification.innerHTML = `<p class="text-sm font-medium">${message}</p>`

      flashContainer.appendChild(notification)

      // Auto-remove after 4 seconds
      setTimeout(() => {
        notification.style.transition = 'opacity 300ms ease-in'
        notification.style.opacity = '0'
        setTimeout(() => notification.remove(), 300)
      }, 4000)
    },

    // Initialize component
    init() {
      const self = this

      // Subscribe to ActionCable for real-time updates
      this.subscription = consumer.subscriptions.create(
        { channel: "DataUpdatesChannel" },
        {
          received(message) {
            if (message.type === 'viewing' && self.viewing && message.id === self.viewing.id) {
              console.debug('[ViewingSlideover] Received update for viewing', message.id)
              self.handleViewingUpdate(message)
            }
          }
        }
      )

      // Listen for open requests
      this.$watch('open', (value) => {
        if (value && this.viewingId && !this.viewing) {
          this.fetchViewing()
        }
      })

      // Listen for create mode requests
      window.addEventListener('open-viewing-slideover-create', (event) => {
        self.openForCreate(event.detail || {})
      })
    },

    // Open slide-over for a specific viewing (view mode)
    openForViewing(viewingId) {
      const store = Alpine.store('slideovers')

      // If this viewing is already open, just bring it to front
      if (store && store.isOpen('viewing', viewingId)) {
        store.bringToFront('viewing', viewingId)
        return
      }

      // Get next z-index from store for proper stacking
      if (store) {
        this.zIndex = store.getNextZIndex()
      }
      this.mode = 'view'
      this.viewingId = viewingId
      this.viewing = null
      this.error = null
      this.openSections = { details: true, history: true }
      // Reset action states
      this.showCancelForm = false
      this.showRescheduleForm = false
      this.showRejectForm = false
      this.cancelReason = ''
      this.rejectReason = ''
      this.cancelling = false
      this.rejecting = false
      this.rescheduling = false
      this.confirming = false
      this.completing = false
      this.markingNoShow = false
      // Reset reschedule form state
      this.rescheduleSlots = []
      this.loadingRescheduleSlots = false
      this.rescheduleDate = ''
      this.rescheduleTime = ''
      this.rescheduleUseCustomTime = false
      this.rescheduleReason = ''
      this.open = true

      // Register this slideover
      if (store) {
        store.register('viewing', viewingId, this)
      }

      this.fetchViewing()
    },

    // Open slide-over for creating a new viewing (book viewing)
    openForCreate(prepopulatedData = {}) {
      // Get next z-index from store for proper stacking
      if (Alpine.store('slideovers')) {
        this.zIndex = Alpine.store('slideovers').getNextZIndex()
      }
      this.mode = 'create'
      this.viewingId = null
      this.viewing = null
      this.error = null
      this.formErrors = {}
      this.saving = false
      this.availableSlots = []
      this.loadingSlots = false

      // Reset form with prepopulated data
      this.form = {
        property_id: prepopulatedData.property_id || null,
        property_search: prepopulatedData.property_name || '',
        property_results: [],
        searching_properties: false,
        show_property_dropdown: false,
        selected_property: prepopulatedData.property_id ? {
          id: prepopulatedData.property_id,
          display_name: prepopulatedData.property_name || '',
          full_address: prepopulatedData.property_address || ''
        } : null,
        person_id: prepopulatedData.person_id || null,
        person_search: prepopulatedData.person_name || '',
        person_results: [],
        searching_people: false,
        show_person_dropdown: false,
        selected_person: prepopulatedData.person_id ? {
          id: prepopulatedData.person_id,
          full_name: prepopulatedData.person_name || '',
          email: prepopulatedData.person_email || '',
          phone: prepopulatedData.person_phone || ''
        } : null,
        show_new_person_form: false,
        new_person: {
          first_name: '',
          last_name: '',
          email: '',
          phone: ''
        },
        enquiry_id: prepopulatedData.enquiry_id || null,
        scheduled_at: '',
        scheduled_date: '',
        scheduled_time: '',
        use_custom_time: false,
        notes: '',
        form_responses: prepopulatedData.form_responses || {}
      }

      this.openSections = {}
      this.open = true
      this.loadingPropertyDetails = false

      // If property is prepopulated, fetch full property details (for availability status etc)
      if (prepopulatedData.property_id) {
        this.loadingPropertyDetails = true
        this.fetchPropertyDetails(prepopulatedData.property_id)
      }
    },

    // Fetch full property details for a prepopulated property
    async fetchPropertyDetails(propertyId) {
      try {
        this.loadingPropertyDetails = true
        // Use search API with the property name to get full details
        const response = await fetch(`/api/properties/search?q=${encodeURIComponent(this.form.property_search)}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (response.ok) {
          const results = await response.json()
          // Find the matching property by ID
          const property = results.find(p => p.id === propertyId)
          if (property) {
            this.form.selected_property = property
            // Fetch available slots if property is available for viewings
            if (property.available_for_viewings) {
              this.fetchAvailableSlots()
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch property details:', error)
      } finally {
        this.loadingPropertyDetails = false
      }
    },

    // Open slide-over for editing an existing viewing
    openForEdit(viewingId) {
      this.mode = 'edit'
      this.viewingId = viewingId
      this.viewing = null
      this.error = null
      this.formErrors = {}
      this.saving = false
      this.open = true
      this.fetchViewing()
    },

    // Close slide-over
    close() {
      // Unregister from store
      const store = Alpine.store('slideovers')
      if (store && this.viewingId) {
        store.unregister('viewing', this.viewingId)
      }

      this.open = false
    },

    // Fetch viewing data from server
    async fetchViewing(silent = false) {
      if (!this.viewingId) return

      if (!silent) {
        this.loading = true
      }
      this.error = null

      try {
        const response = await fetch(`/viewings/${this.viewingId}.json`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        this.viewing = await response.json()
        this.viewNotes = this.viewing.notes || ''

        // If in edit mode, populate form from viewing data
        if (this.mode === 'edit') {
          this.populateFormFromViewing()
        }
      } catch (error) {
        console.error('Failed to fetch viewing:', error)
        this.error = 'Failed to load viewing details. Please try again.'
      } finally {
        this.loading = false
      }
    },

    // Populate form fields from viewing data (for edit mode)
    populateFormFromViewing() {
      if (!this.viewing) return

      const scheduledAt = this.viewing.scheduled_at ? new Date(this.viewing.scheduled_at) : null

      this.form = {
        property_id: this.viewing.property?.id,
        property_search: '',
        property_results: [],
        searching_properties: false,
        show_property_dropdown: false,
        selected_property: this.viewing.property,
        person_id: this.viewing.person?.id,
        person_search: '',
        person_results: [],
        searching_people: false,
        show_person_dropdown: false,
        selected_person: this.viewing.person,
        enquiry_id: this.viewing.property_enquiry_id,
        scheduled_at: this.viewing.scheduled_at || '',
        scheduled_date: scheduledAt ? scheduledAt.toISOString().split('T')[0] : '',
        scheduled_time: scheduledAt ? scheduledAt.toTimeString().slice(0, 5) : '',
        notes: this.viewing.notes || '',
        form_responses: this.viewing.form_responses || {}
      }
    },

    // Handle real-time updates from ActionCable
    handleViewingUpdate(message) {
      if (!this.viewing || message.id !== this.viewing.id) return

      switch (message.action) {
        case 'update':
          Object.assign(this.viewing, message.data)
          break
        case 'destroy':
          this.close()
          break
      }
    },

    // Computed helpers for template
    get statusBadgeColor() {
      if (!this.viewing) return 'gray'
      switch (this.viewing.status) {
        case 'confirmed': return 'green'
        case 'pending_approval': return 'amber'
        case 'completed': return 'blue'
        case 'cancelled': return 'red'
        case 'no_show': return 'red'
        default: return 'gray'
      }
    },

    get statusBadgeText() {
      if (!this.viewing) return ''
      const statusMap = {
        'pending_approval': 'Pending Approval',
        'confirmed': 'Confirmed',
        'completed': 'Completed',
        'cancelled': 'Cancelled',
        'no_show': 'No Show',
        'rejected': 'Rejected'
      }
      return statusMap[this.viewing.status] || this.viewing.status
    },

    // Format date/time
    formatDateTime(dateString) {
      if (!dateString) return '—'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    formatDate(dateString) {
      if (!dateString) return '—'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    },

    formatTime(dateString) {
      if (!dateString) return '—'
      const date = new Date(dateString)
      return date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    // Search for properties
    async searchProperties() {
      const query = this.form.property_search
      if (!query || query.length < 2) {
        this.form.property_results = []
        this.form.show_property_dropdown = false
        return
      }

      this.form.searching_properties = true
      try {
        const response = await fetch(`/api/properties/search?q=${encodeURIComponent(query)}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (response.ok) {
          this.form.property_results = await response.json()
          this.form.show_property_dropdown = true
        }
      } catch (error) {
        console.error('Failed to search properties:', error)
      } finally {
        this.form.searching_properties = false
      }
    },

    // Select a property from search results
    selectProperty(property) {
      this.form.property_id = property.id
      this.form.selected_property = property
      this.form.property_search = property.display_name
      this.form.property_results = []
      this.form.show_property_dropdown = false
      this.clearFieldError('property_id')

      // Fetch available slots for the selected property
      this.fetchAvailableSlots()
    },

    // Clear selected property
    clearProperty() {
      this.form.property_id = null
      this.form.selected_property = null
      this.form.property_search = ''
      this.form.property_results = []
      this.availableSlots = []
      this.form.scheduled_date = ''
      this.form.scheduled_time = ''
    },

    // Fetch available viewing slots for the selected property
    async fetchAvailableSlots() {
      if (!this.form.property_id) {
        this.availableSlots = []
        return
      }

      this.loadingSlots = true
      try {
        // Fetch slots for next 14 days
        const from = new Date()
        const to = new Date()
        to.setDate(to.getDate() + 14)

        const fromStr = from.toISOString().split('T')[0]
        const toStr = to.toISOString().split('T')[0]

        const response = await fetch(`/api/viewing_slots?property_id=${this.form.property_id}&from=${fromStr}&to=${toStr}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (response.ok) {
          const data = await response.json()
          this.availableSlots = data.slots || []
        } else {
          this.availableSlots = []
        }
      } catch (error) {
        console.error('Failed to fetch available slots:', error)
        this.availableSlots = []
      } finally {
        this.loadingSlots = false
      }
    },

    // Get today's date in YYYY-MM-DD format
    get today() {
      return new Date().toISOString().split('T')[0]
    },

    // Get unique dates that have available slots
    get availableDates() {
      const dates = new Map()
      for (const slot of this.availableSlots) {
        const dateStr = slot.date
        if (!dates.has(dateStr)) {
          const date = new Date(dateStr + 'T00:00:00')
          dates.set(dateStr, {
            value: dateStr,
            day: date.toLocaleDateString('en-GB', { weekday: 'short' }),
            date: date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
          })
        }
      }
      return Array.from(dates.values()).slice(0, 7) // Show up to 7 days
    },

    // Get slots for the currently selected date
    get slotsForSelectedDate() {
      if (!this.form.scheduled_date) return []
      return this.availableSlots
        .filter(slot => slot.date === this.form.scheduled_date)
        .sort((a, b) => a.time.localeCompare(b.time))
    },

    // Select a date from the date picker
    selectDate(dateValue) {
      this.form.scheduled_date = dateValue
      this.form.scheduled_time = ''
      this.clearFieldError('scheduled_at')
    },

    // Select a time slot
    selectTimeSlot(time) {
      this.form.scheduled_time = time
      this.clearFieldError('scheduled_at')
    },

    // Handle custom datetime picker change (Flatpickr)
    handleCustomDatetimeChange(event) {
      const value = event.target.value // Format: "YYYY-MM-DD HH:mm"
      if (value) {
        const [datePart, timePart] = value.split(' ')
        this.form.scheduled_date = datePart
        this.form.scheduled_time = timePart
        this.clearFieldError('scheduled_at')
      }
    },

    // Search for people
    async searchPeople() {
      const query = this.form.person_search
      if (!query || query.length < 2) {
        this.form.person_results = []
        this.form.show_person_dropdown = false
        return
      }

      this.form.searching_people = true
      try {
        // Search the Person model (enquirers, viewers, tenants)
        const response = await fetch(`/api/people/search?q=${encodeURIComponent(query)}&types[]=person`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (response.ok) {
          this.form.person_results = await response.json()
          this.form.show_person_dropdown = true
        }
      } catch (error) {
        console.error('Failed to search people:', error)
      } finally {
        this.form.searching_people = false
      }
    },

    // Select a person from search results
    selectPerson(person) {
      this.form.person_id = person.id
      this.form.selected_person = person
      this.form.person_search = person.full_name || person.name
      this.form.person_results = []
      this.form.show_person_dropdown = false
      this.clearFieldError('person_id')
    },

    // Clear selected person
    clearPerson() {
      this.form.person_id = null
      this.form.selected_person = null
      this.form.person_search = ''
      this.form.person_results = []
    },

    // Show the inline new person form
    showNewPersonForm() {
      this.form.show_new_person_form = true
      this.form.new_person = {
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      }
    },

    // Cancel and hide the new person form
    cancelNewPerson() {
      this.form.show_new_person_form = false
      this.form.new_person = {
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      }
    },

    // Create a new person and select them
    async addNewPerson() {
      const { first_name, last_name, email, phone } = this.form.new_person

      if (!first_name || !last_name || !email || !phone) {
        return
      }

      try {
        const response = await fetch('/api/people', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            person: {
              first_name,
              last_name,
              email,
              phone
            }
          })
        })

        if (response.ok) {
          const person = await response.json()
          this.form.person_id = person.id
          this.form.selected_person = person
          this.form.show_new_person_form = false
          this.form.new_person = { first_name: '', last_name: '', email: '', phone: '' }
          this.clearFieldError('person_id')
        } else {
          const data = await response.json()
          console.error('Failed to create person:', data.errors)
          // Could show error to user here
        }
      } catch (error) {
        console.error('Failed to create person:', error)
      }
    },

    // Humanize field names for error display
    humanizeFieldName(field) {
      const fieldNames = {
        property_id: 'Property',
        person_id: 'Viewer',
        scheduled_at: 'Date/Time',
        scheduled_date: 'Date',
        scheduled_time: 'Time',
        notes: 'Notes',
        base: 'Form'
      }
      return fieldNames[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },

    // Validate the form
    validateForm() {
      this.formErrors = {}

      if (!this.form.property_id) {
        this.formErrors.property_id = 'Property is required'
      }

      if (!this.form.person_id) {
        this.formErrors.person_id = 'Viewer is required'
      }

      if (!this.form.scheduled_date || !this.form.scheduled_time) {
        this.formErrors.scheduled_at = 'Date and time are required'
      }

      return Object.keys(this.formErrors).length === 0
    },

    // Clear a specific field error
    clearFieldError(field) {
      delete this.formErrors[field]
    },

    // Build the scheduled_at datetime from date and time fields
    buildScheduledAt() {
      if (!this.form.scheduled_date || !this.form.scheduled_time) return null
      return `${this.form.scheduled_date}T${this.form.scheduled_time}:00`
    },

    // Create a new viewing
    async createViewing() {
      if (!this.validateForm()) return

      this.saving = true
      this.formErrors = {}

      try {
        const response = await fetch('/viewings.json', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            viewing: {
              property_id: this.form.property_id,
              person_id: this.form.person_id,
              property_enquiry_id: this.form.enquiry_id,
              scheduled_at: this.buildScheduledAt(),
              notes: this.form.notes,
              form_responses: this.form.form_responses
            }
          })
        })

        const data = await response.json()

        if (response.ok) {
          // Switch to view mode showing the new viewing
          this.mode = 'view'
          this.viewingId = data.id
          this.viewing = data
          this.openSections = { details: true, history: true }

          // Dispatch event for list updates
          window.dispatchEvent(new CustomEvent('viewing-created', {
            detail: { viewing: data }
          }))
        } else {
          this.handleFormErrors(data.errors || { base: 'Failed to create viewing' })
        }
      } catch (error) {
        console.error('Failed to create viewing:', error)
        this.formErrors.base = 'An unexpected error occurred. Please try again.'
      } finally {
        this.saving = false
      }
    },

    // Update an existing viewing
    async updateViewing() {
      if (!this.validateForm()) return

      this.saving = true
      this.formErrors = {}

      try {
        const response = await fetch(`/viewings/${this.viewingId}.json`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            viewing: {
              scheduled_at: this.buildScheduledAt(),
              notes: this.form.notes
            }
          })
        })

        const data = await response.json()

        if (response.ok) {
          // Switch back to view mode
          this.mode = 'view'
          this.viewing = data

          // Dispatch event for list updates
          window.dispatchEvent(new CustomEvent('viewing-updated', {
            detail: { viewing: data }
          }))
        } else {
          this.handleFormErrors(data.errors || { base: 'Failed to update viewing' })
        }
      } catch (error) {
        console.error('Failed to update viewing:', error)
        this.formErrors.base = 'An unexpected error occurred. Please try again.'
      } finally {
        this.saving = false
      }
    },

    // Handle form errors from server
    handleFormErrors(errors) {
      if (typeof errors === 'object') {
        Object.entries(errors).forEach(([field, messages]) => {
          this.formErrors[field] = Array.isArray(messages) ? messages[0] : messages
        })
      } else {
        this.formErrors.base = 'Failed to save viewing'
      }
    },

    // Confirm a pending viewing
    async confirmViewing() {
      if (!this.viewing) return

      this.confirming = true
      try {
        const response = await fetch(`/viewings/${this.viewing.id}/confirm.json`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (response.ok) {
          const data = await response.json()
          this.viewing = data.viewing || data

          window.dispatchEvent(new CustomEvent('viewing-updated', {
            detail: { viewing: this.viewing }
          }))

          this.showNotification('Viewing confirmed successfully')
        } else {
          this.showNotification('Failed to confirm viewing', 'error')
        }
      } catch (error) {
        console.error('Failed to confirm viewing:', error)
        this.showNotification('Failed to confirm viewing', 'error')
      } finally {
        this.confirming = false
      }
    },

    // Reject a pending viewing
    async rejectViewing() {
      if (!this.viewing) return

      this.rejecting = true
      try {
        const response = await fetch(`/viewings/${this.viewing.id}/reject.json`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            reason: this.rejectReason
          })
        })

        if (response.ok) {
          const data = await response.json()
          this.viewing = data.viewing || data

          // Reset the form
          this.showRejectForm = false
          this.rejectReason = ''

          window.dispatchEvent(new CustomEvent('viewing-updated', {
            detail: { viewing: this.viewing }
          }))

          this.showNotification('Viewing rejected')
        } else {
          this.showNotification('Failed to reject viewing', 'error')
        }
      } catch (error) {
        console.error('Failed to reject viewing:', error)
        this.showNotification('Failed to reject viewing', 'error')
      } finally {
        this.rejecting = false
      }
    },

    // Cancel a viewing
    async cancelViewing() {
      if (!this.viewing) return

      this.cancelling = true
      try {
        const response = await fetch(`/viewings/${this.viewing.id}/cancel.json`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            reason: this.cancelReason
          })
        })

        if (response.ok) {
          const data = await response.json()
          this.viewing = data.viewing || data

          // Reset the form
          this.showCancelForm = false
          this.cancelReason = ''

          window.dispatchEvent(new CustomEvent('viewing-updated', {
            detail: { viewing: this.viewing }
          }))

          this.showNotification('Viewing cancelled')
        } else {
          const data = await response.json().catch(() => ({}))
          this.showNotification(data.error || 'Failed to cancel viewing', 'error')
        }
      } catch (error) {
        console.error('Failed to cancel viewing:', error)
        this.showNotification('Failed to cancel viewing', 'error')
      } finally {
        this.cancelling = false
      }
    },

    // Mark viewing as completed
    async completeViewing() {
      if (!this.viewing) return

      this.completing = true
      try {
        const response = await fetch(`/viewings/${this.viewing.id}/complete.json`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (response.ok) {
          const data = await response.json()
          this.viewing = data.viewing || data

          window.dispatchEvent(new CustomEvent('viewing-updated', {
            detail: { viewing: this.viewing }
          }))

          this.showNotification('Viewing marked as completed')
        } else {
          this.showNotification('Failed to complete viewing', 'error')
        }
      } catch (error) {
        console.error('Failed to complete viewing:', error)
        this.showNotification('Failed to complete viewing', 'error')
      } finally {
        this.completing = false
      }
    },

    // Mark viewing as no-show
    async markNoShow() {
      if (!this.viewing) return

      this.markingNoShow = true
      try {
        const response = await fetch(`/viewings/${this.viewing.id}/no_show.json`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (response.ok) {
          const data = await response.json()
          this.viewing = data.viewing || data

          window.dispatchEvent(new CustomEvent('viewing-updated', {
            detail: { viewing: this.viewing }
          }))

          this.showNotification('Viewing marked as no-show')
        } else {
          this.showNotification('Failed to mark no-show', 'error')
        }
      } catch (error) {
        console.error('Failed to mark no-show:', error)
        this.showNotification('Failed to mark no-show', 'error')
      } finally {
        this.markingNoShow = false
      }
    },

    // Send post-viewing email to viewer
    async sendPostViewingEmail() {
      if (!this.viewing) return

      this.sendingPostViewingEmail = true
      try {
        const response = await fetch(`/viewings/${this.viewing.id}/send_post_viewing_email.json`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (response.ok) {
          const data = await response.json()
          this.viewing = data.viewing || data

          window.dispatchEvent(new CustomEvent('viewing-updated', {
            detail: { viewing: this.viewing }
          }))

          this.showNotification('Post-viewing email queued')
        } else {
          const errorData = await response.json()
          this.showNotification(errorData.error || 'Failed to send email', 'error')
        }
      } catch (error) {
        console.error('Failed to send post-viewing email:', error)
        this.showNotification('Failed to send email', 'error')
      } finally {
        this.sendingPostViewingEmail = false
      }
    },

    // Save notes (auto-save on change)
    async saveNotes() {
      if (!this.viewing || this.savingNotes) return
      if (this.viewNotes === (this.viewing.notes || '')) return // No change

      this.savingNotes = true

      try {
        const response = await fetch(`/viewings/${this.viewing.id}.json`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            viewing: {
              notes: this.viewNotes
            }
          })
        })

        if (response.ok) {
          const data = await response.json()
          this.viewing = data
        }
      } catch (error) {
        console.error('Failed to save notes:', error)
      } finally {
        this.savingNotes = false
      }
    },

    // Open reschedule form and fetch available slots
    async openRescheduleForm() {
      // Reset state
      this.rescheduleDate = ''
      this.rescheduleTime = ''
      this.rescheduleUseCustomTime = false
      this.rescheduleSlots = []
      this.rescheduleReason = ''

      // Fetch data first, then show form (prevents animation jump)
      this.loadingRescheduleSlots = true
      await this.fetchRescheduleSlots()

      // Now show the form with data already loaded
      this.showRescheduleForm = true
    },

    // Close reschedule form and reset state
    closeRescheduleForm() {
      this.showRescheduleForm = false
      this.rescheduleDate = ''
      this.rescheduleTime = ''
      this.rescheduleUseCustomTime = false
      this.rescheduleSlots = []
      this.rescheduleReason = ''
    },

    // Fetch available slots for rescheduling
    async fetchRescheduleSlots() {
      if (!this.viewing || !this.viewing.property_id) {
        this.rescheduleSlots = []
        return
      }

      this.loadingRescheduleSlots = true
      try {
        // Fetch slots for next 14 days
        const from = new Date()
        const to = new Date()
        to.setDate(to.getDate() + 14)

        const fromStr = from.toISOString().split('T')[0]
        const toStr = to.toISOString().split('T')[0]

        const response = await fetch(`/api/viewing_slots?property_id=${this.viewing.property_id}&from=${fromStr}&to=${toStr}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (response.ok) {
          const data = await response.json()
          this.rescheduleSlots = data.slots || []
        } else {
          this.rescheduleSlots = []
        }
      } catch (error) {
        console.error('Failed to fetch reschedule slots:', error)
        this.rescheduleSlots = []
      } finally {
        this.loadingRescheduleSlots = false
      }
    },

    // Get unique dates that have available slots for rescheduling
    get rescheduleAvailableDates() {
      const dates = new Map()
      for (const slot of this.rescheduleSlots) {
        const dateStr = slot.date
        if (!dates.has(dateStr)) {
          const date = new Date(dateStr + 'T00:00:00')
          dates.set(dateStr, {
            value: dateStr,
            day: date.toLocaleDateString('en-GB', { weekday: 'short' }),
            date: date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
          })
        }
      }
      return Array.from(dates.values()).slice(0, 7) // Show up to 7 days
    },

    // Get slots for the currently selected reschedule date
    get rescheduleSlotsForSelectedDate() {
      if (!this.rescheduleDate) return []
      return this.rescheduleSlots
        .filter(slot => slot.date === this.rescheduleDate)
        .sort((a, b) => a.time.localeCompare(b.time))
    },

    // Select a date for rescheduling
    selectRescheduleDate(dateValue) {
      this.rescheduleDate = dateValue
      this.rescheduleTime = ''
    },

    // Select a time slot for rescheduling
    selectRescheduleTimeSlot(time) {
      this.rescheduleTime = time
    },

    // Handle custom datetime picker change for rescheduling
    handleRescheduleCustomDatetimeChange(event) {
      const value = event.target.value // Format: "YYYY-MM-DD HH:mm"
      if (value) {
        const [datePart, timePart] = value.split(' ')
        this.rescheduleDate = datePart
        this.rescheduleTime = timePart
      }
    },

    // Reschedule a viewing to a new time
    async rescheduleViewing() {
      if (!this.viewing || !this.rescheduleDate || !this.rescheduleTime) return

      this.rescheduling = true

      const scheduledAt = `${this.rescheduleDate}T${this.rescheduleTime}:00`
      let response
      let data

      // Network request - catch errors here
      try {
        response = await fetch(`/viewings/${this.viewing.id}.json`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            viewing: {
              scheduled_at: scheduledAt,
              reschedule_reason: this.rescheduleReason || null
            }
          })
        })

        if (response.ok) {
          data = await response.json()
        } else {
          // Show validation error to user
          const errorData = await response.json()
          const errorMessage = errorData.errors?.scheduled_at?.[0] ||
                               errorData.errors?.base?.[0] ||
                               Object.values(errorData.errors || {}).flat()[0] ||
                               'Failed to reschedule viewing'
          this.showNotification(`Reschedule failed: ${errorMessage}`, 'error')
          console.error('Failed to reschedule viewing:', errorData.errors)
          this.rescheduling = false
          return
        }
      } catch (error) {
        console.error('Failed to reschedule viewing (network error):', error)
        this.showNotification('Failed to reschedule viewing. Please try again.', 'error')
        this.rescheduling = false
        return
      }

      // Update UI - don't alert on render errors, just log them
      try {
        this.viewing = data

        // Reset the form
        this.closeRescheduleForm()

        window.dispatchEvent(new CustomEvent('viewing-updated', {
          detail: { viewing: this.viewing }
        }))

        this.showNotification('Viewing rescheduled successfully')
      } catch (renderError) {
        // Log render errors but don't alert - data was saved successfully
        console.error('Error updating UI after reschedule (data was saved):', renderError)
      }

      this.rescheduling = false
    },

    // Check if the viewing is in the past
    isPastViewing() {
      if (!this.viewing || !this.viewing.scheduled_at) return false
      return new Date(this.viewing.scheduled_at) < new Date()
    }
  }
}
